export enum SoapNoteStatus {
  DRAFT = 'DRAFT',
  COMPLETED = 'COMPLETED',
}

export interface SoapVitals {
  temperature?: { value?: number; unit: string; normal?: string };
  pulse?: { value?: number; unit: string; normal?: string };
  respiration?: { value?: number; unit: string; normal?: string };
  weight?: { value?: number; unit: string };
}

export interface SoapPhysicalExam {
  general?: string[];
  cardiovascular?: string[];
  respiratory?: string[];
  gastrointestinal?: string[];
  musculoskeletal?: string[];
  integumentary?: string[];
  neurological?: string[];
  // New field for AI-generated bullet points
  aiFindings?: string;
}

export interface SoapLamenessEval {
  gradeScale?: string;
  observations?: {
    walk?: string[];
    trot?: string[];
    circles?: string[];
    surfaces?: string[];
  };
  palpation?: string[];
  flexionTests?: string[];
  hoofTester?: string[];
}

export interface SoapSubjective {
  prompts?: string[];
  responses?: Record<string, string>;
  notes?: string;
  // New AI-generated field with formatted findings
  aiSummary?: string;
}

export interface SoapObjective {
  vitals?: SoapVitals;
  physicalExam?: SoapPhysicalExam;
  lamenessEval?: SoapLamenessEval;
  findings?: string;
  notes?: string;
  // New AI-generated field with all objective findings
  aiSummary?: string;
}

export interface SoapAssessment {
  primaryDiagnosis?: string;
  differentials?: string[];
  prognosis?: string;
  notes?: string;
  // New AI-generated field with formatted assessment
  aiSummary?: string;
}

export interface SoapTreatment {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  route?: string;
  notes?: string;
}

export interface SoapProcedure {
  name: string;
  notes?: string;
}

export interface SoapPlan {
  diagnosticsOrdered?: string[];
  treatments?: SoapTreatment[];
  medicationsPrescribed?: SoapTreatment[];
  proceduresPrescribed?: SoapProcedure[];
  followUpDate?: Date | string;
  followUpInstructions?: string; // Keep for backward compatibility
  clientEducation?: string; // Keep for backward compatibility
  notes?: string; // Keep for backward compatibility
  // New unified field for all plan notes (follow-up instructions, client education, additional notes)
  planNotes?: string;
}

export interface SoapNote {
  id: string;
  status: SoapNoteStatus;
  horseId: string;
  providerId: string;
  originalTranscript?: string;
  clinicId: string;
  subjective?: SoapSubjective;
  objective?: SoapObjective;
  assessment?: SoapAssessment;
  plan?: SoapPlan;
  date: Date | string;
  completedAt?: Date | string;
  lastAutosavedAt?: Date | string;
  templateUsed?: string;
  billingInfo?: any;
  isArchived: boolean;
  archivedAt?: Date | string;
  archivedBy?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  horse?: any; // Will be populated with Horse data
  provider?: {
    id: string;
    name: string;
    email: string;
    professionalTitle?: string;
  };
}

export interface SoapTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  isDefault: boolean;
  isSystemDefault?: boolean;
  examType?: string;
  schema?: any[];
  subjective?: any;
  objective?: any;
  assessment?: any;
  plan?: any;
  clinicId?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface CreateSoapNoteDto {
  id?: string; // For upsert operations
  horseId: string;
  date: Date | string;
  templateUsed?: string;
  subjective?: SoapSubjective;
  objective?: SoapObjective;
  assessment?: SoapAssessment;
  plan?: SoapPlan;
  originalTranscript?: string;
  status?: SoapNoteStatus;
  // Optional link to the visit's appointment (barn-visit workflow).
  appointmentId?: string;
}

export interface UpdateSoapNoteDto extends Partial<CreateSoapNoteDto> {
  status?: SoapNoteStatus;
}

export interface MedicationToAdd {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  route?: string;
  quantity?: number;
  instructions?: string;
  startDate?: string;
  endDate?: string;
  // ─── Take-home Rx fields (set in the SOAP medication confirmation step) ───
  // When isRx is true the medication is created as a real take-home Rx —
  // refillable, dispensable via the Refill button, label-printable — instead
  // of a flat in-clinic record. The vet flips this per-medication while
  // confirming a SOAP note's medications.
  isRx?: boolean;
  refillsAuthorized?: number;
  // ISO date; backend defaults to startDate + 12 months when omitted.
  expirationDate?: string;
  // DEA controlled-substance schedule ("II"–"V"), or "" / undefined.
  deaSchedule?: string;
  // Bottle (ControlledSubstanceLot.id) for the DEA auto-log when the name
  // matches the clinic's CS registry; undefined = server FIFO pick (ADR-0014).
  deaLotId?: string;
}

export interface ProcedureToAdd {
  name: string;
  notes?: string;
  date?: string;
}

export interface CompleteSoapNoteDto {
  subjective?: SoapSubjective;
  objective?: SoapObjective;
  assessment?: SoapAssessment;
  plan?: SoapPlan;
  billingInfo?: any;
  medications?: MedicationToAdd[];
  procedures?: ProcedureToAdd[];
}
